import { useMemo } from "react";
import { useServices } from "@core/servicesContext";
import { StorePresentation } from "@core/store/storePresentation";
import {
	AsyncResult,
	PaginatedDataResult,
	PromiseResult,
	useMappedStore,
	usePaginatedStore,
	usePromise,
	useStoredData,
} from "@utils/hooks";
import { useCurrentProfile } from "@domain/currentProfile/hooks";
import { Course, DetailedCourse } from "./course";
import { CourseStatus } from "./courseDraft";
import { Feedback } from "@domain/lms/course/feedback/feedback";

export function useCourse(id: string, slug?: string): AsyncResult<Course> {
	const { courseService } = useServices();
	const currentProfile = useCurrentProfile();

	return useStoredData(slug ?? id, slug ? courseService.slugCourseStore : courseService.courseStore, [
		currentProfile?.id,
		slug,
	]);
}

export function useSlugCourse(slug: string): AsyncResult<Course> {
	const { courseService } = useServices();
	const currentProfile = useCurrentProfile();

	return useStoredData(slug, courseService.slugCourseStore, [currentProfile?.id]);
}

export function useDetailedCourse(id: string): AsyncResult<DetailedCourse> {
	const { courseService } = useServices();
	const currentProfile = useCurrentProfile();

	return useStoredData(id, courseService.detailedCourseStore, [currentProfile?.id]);
}

export function useFavoriteCourses(userId: string): PaginatedDataResult<Course> {
	const { courseService } = useServices();
	return usePaginatedStore(courseService.favoriteCoursesPresentation, [userId]);
}

export function useSuggestedCourses(count: number): PaginatedDataResult<Course> {
	const { courseService } = useServices();

	const store = useMemo(
		() => new StorePresentation(courseService.courseStore, () => courseService.suggest(count)),
		[count]
	);
	return usePaginatedStore(store, [count]);
}

export function useCourseFeedbacks(postId: string): PaginatedDataResult<Feedback> {
	const { courseService } = useServices();
	return useMappedStore(postId, courseService.feedbackStore, [postId]);
}

export function useMyCourses(status: CourseStatus | "ALL"): PaginatedDataResult<Course> {
	const { courseService } = useServices();
	return useMappedStore(status, courseService.myCoursesStore, [status]);
}

export function usePersonCourses(personId: string, organization?: boolean): PaginatedDataResult<Course> {
	const { courseService } = useServices();
	return useMappedStore(
		personId,
		organization ? courseService.organizationCoursesStore : courseService.profileCoursesStore,
		[personId, organization]
	);
}

export function useWorkspaceCourses(workspaceId: string): PaginatedDataResult<Course> {
	const { courseService } = useServices();
	return useMappedStore(workspaceId, courseService.workspaceCoursesStore, [workspaceId]);
}

export function useMyFeedback(courseId: string): PromiseResult<Feedback | null> {
	const { courseService } = useServices();
	return usePromise(() => courseService.getMyFeedback(courseId), [courseId]);
}
